import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type RootState } from '../../../services/serviceTypes';
import { useGenerateInstructionsMutation } from '../data-store/copilotApi';
import {
  addMessage,
  addMessageToEntitlement,
  removeMessagesBySubType,
  setPromptFooterEnabled,
} from '../data-store/copilotSlice';
import { convertFileToBase64 } from '../utils/convertFileToBase64';

import { useChatFlowController } from './useChatFlowController';

/**
 * Custom hook for handling file submission and prompt processing
 */
const useFileSubmit = () => {
  const dispatch = useDispatch();
  const { controller } = useChatFlowController();
  const [generateInstructions] = useGenerateInstructionsMutation();

  // Get current entitlement context for proper message cleanup
  const { addAccessCurrentIndex, removeAccessCurrentIndex, activeStep } =
    useSelector((state: RootState) => ({
      addAccessCurrentIndex:
        state.copilot.entitlementIteration.currentIndex ?? 0,
      removeAccessCurrentIndex: state.copilot.removeAccessEntitlementIndex ?? 0,
      activeStep: state.copilot.navigation.activeStep,
    }));

  const handleFileSubmit = useCallback(
    (file: File | null, prompt: string) => {
      // Handle async operations in IIFE to avoid returning Promise
      (async () => {
        // Determine if we're in AddAccess or RemoveAccess flow
        const isAddAccessFlow = activeStep.startsWith('AddAccess_');
        const isRemoveAccessFlow =
          activeStep === 'RemoveAccess' ||
          activeStep.startsWith('RemoveAccess_');

        const correctEntitlementIndex = isRemoveAccessFlow
          ? removeAccessCurrentIndex
          : addAccessCurrentIndex;

        // Create file_prompt message if file or prompt exists
        if (file || prompt.trim()) {
          const messageData: {
            id: string;
            type: string;
            subType: string;
            file?: File;
            userInput?: string;
            timestamp: number;
            parentEntitlementIndex?: number;
            entitlementType?: string;
          } = {
            id: `file-prompt-${Date.now()}`,
            type: 'file_prompt',
            subType: 'file_display',
            file: file ?? undefined,
            userInput: prompt.trim() || undefined,
            timestamp: Date.now(),
          };

          // Add parent entitlement properties for proper cleanup
          if (isAddAccessFlow) {
            messageData.parentEntitlementIndex = correctEntitlementIndex;
            messageData.entitlementType = 'addAccess';
          } else if (isRemoveAccessFlow) {
            messageData.parentEntitlementIndex = correctEntitlementIndex;
            messageData.entitlementType = 'removeAccess';
          }

          if (isAddAccessFlow || isRemoveAccessFlow) {
            dispatch(
              addMessageToEntitlement({
                entitlementIndex: correctEntitlementIndex,
                entitlementType: isAddAccessFlow ? 'addAccess' : 'removeAccess',
                message: messageData as Parameters<
                  typeof addMessageToEntitlement
                >[0]['message'],
              })
            );
          } else {
            dispatch(
              addMessage(messageData as Parameters<typeof addMessage>[0])
            );
          }
        }

        // Handle file upload API call
        if (file) {
          const analyzingMessageId = `analyzing-${Date.now()}`;

          try {
            // Show analyzing message
            const analyzingMessage = {
              id: analyzingMessageId,
              type: 'ai_response' as const,
              subType: 'analyzing' as const,
              timestamp: Date.now(),
              loader: true,
            };

            if (isAddAccessFlow || isRemoveAccessFlow) {
              dispatch(
                addMessageToEntitlement({
                  entitlementIndex: correctEntitlementIndex,
                  entitlementType: isAddAccessFlow
                    ? 'addAccess'
                    : 'removeAccess',
                  message: analyzingMessage as Parameters<
                    typeof addMessageToEntitlement
                  >[0]['message'],
                })
              );
            } else {
              dispatch(
                addMessage(analyzingMessage as Parameters<typeof addMessage>[0])
              );
            }

            // Convert file to base64 and call API
            const base64Content = await convertFileToBase64(file);
            const response = await generateInstructions({
              fileContent: base64Content,
              fileName: file.name,
              contentType: file.type || 'application/json',
            }).unwrap();

            // Remove analyzing message
            dispatch(removeMessagesBySubType('analyzing'));

            // Disable prompt footer while waiting for Yes/No response
            dispatch(setPromptFooterEnabled(false));

            // Format the response with introduction and confirmation
            const formattedMessage = `Here I extracted instruction from your file.\n${response.message}\n\nDo the above instructions align with your expectations?`;

            // Show API response with proper message and Yes/No buttons
            const fileExtractionMessage: {
              id: string;
              type: string;
              subType: string;
              text: string;
              buttons: { label: string; action: string }[];
              timestamp: number;
              parentEntitlementIndex?: number;
              entitlementType?: string;
            } = {
              id: `instructions-${Date.now()}`,
              type: 'ai_response',
              subType: 'fileExtraction',
              text: formattedMessage,
              buttons: [
                {
                  label: 'Yes',
                  action: 'file_instructions_yes',
                },
                {
                  label: 'No',
                  action: 'file_instructions_no',
                },
              ],
              timestamp: Date.now(),
            };

            // Add parent entitlement properties for proper cleanup
            if (isAddAccessFlow) {
              fileExtractionMessage.parentEntitlementIndex =
                correctEntitlementIndex;
              fileExtractionMessage.entitlementType = 'addAccess';
            } else if (isRemoveAccessFlow) {
              fileExtractionMessage.parentEntitlementIndex =
                correctEntitlementIndex;
              fileExtractionMessage.entitlementType = 'removeAccess';
            }

            if (isAddAccessFlow || isRemoveAccessFlow) {
              dispatch(
                addMessageToEntitlement({
                  entitlementIndex: correctEntitlementIndex,
                  entitlementType: isAddAccessFlow
                    ? 'addAccess'
                    : 'removeAccess',
                  message: fileExtractionMessage as Parameters<
                    typeof addMessageToEntitlement
                  >[0]['message'],
                })
              );
            } else {
              dispatch(
                addMessage(
                  fileExtractionMessage as Parameters<typeof addMessage>[0]
                )
              );
            }
          } catch (error) {
            // Remove analyzing message
            dispatch(removeMessagesBySubType('analyzing'));

            dispatch(setPromptFooterEnabled(true));

            // Show error message
            const errorMessage = {
              id: `connection-error-${Date.now()}`,
              type: 'ai_response' as const,
              subType: 'error' as const,
              text: 'Failed to process file. Try with prompt',
              timestamp: Date.now(),
            };

            if (isAddAccessFlow || isRemoveAccessFlow) {
              dispatch(
                addMessageToEntitlement({
                  entitlementIndex: correctEntitlementIndex,
                  entitlementType: isAddAccessFlow
                    ? 'addAccess'
                    : 'removeAccess',
                  message: errorMessage as Parameters<
                    typeof addMessageToEntitlement
                  >[0]['message'],
                })
              );
            } else {
              dispatch(
                addMessage(errorMessage as Parameters<typeof addMessage>[0])
              );
            }
          }
        }

        // Handle user prompt input
        if (prompt.trim()) {
          // Disable PromptFooter immediately
          dispatch(setPromptFooterEnabled(false));

          // Add AI response with loader after delay
          setTimeout(() => {
            dispatch(
              addMessage({
                id: `moving-next-${Date.now()}`,
                type: 'ai_response',
                subType: 'movingToNextStep',
                timestamp: Date.now(),
                loader: false,
              })
            );

            // Call user prompt flow after another delay
            setTimeout(() => {
              if (
                controller &&
                'handleUserPrompt' in controller &&
                typeof controller.handleUserPrompt === 'function'
              ) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                const result = controller.handleUserPrompt(prompt.trim());

                if (result && typeof result.catch === 'function') {
                  result.catch(() => {
                    // Handle errors silently
                  });
                }
              }
            }, 1000);
          }, 1000);
        }
      })().catch(() => {
        // Handle any unexpected errors silently
      });
    },
    [
      dispatch,
      controller,
      generateInstructions,
      addAccessCurrentIndex,
      removeAccessCurrentIndex,
      activeStep,
    ]
  );

  return { handleFileSubmit };
};

export { useFileSubmit };
export default useFileSubmit;
